// Pantallas integradas — regla del proyecto (2026-10-10): sin ventanas modales.
//
// · PanelIntegrado: sección dentro de la página (título, «✕ Cerrar», contenido),
//   sin fondo oscuro ni ventana encima. Al aparecer se desplaza hasta quedar a la
//   vista. Los «Modal» de los kits de cada módulo (inventario, pos, control
//   bancario, hotel) lo usan por dentro.
// · useDialogoEnLinea: preguntas dentro de la pantalla en lugar de
//   window.confirm / window.prompt / window.alert.
//     const dlg = useDialogoEnLinea();     …     {dlg.ui}   (donde deba verse)
//     if (!(await dlg.confirmar('¿Eliminar el registro?', { peligro: true }))) return;
//     const v = await dlg.pedir('Motivo', [{ key: 'motivo', label: 'Motivo', minimo: 5 }]);  // null = canceló
//     await dlg.avisar('No se puede borrar: tiene movimientos');
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from '@/lib/hooks/useTranslation';

export function PanelIntegrado({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, []);
  return (
    <div ref={ref} className={`w-full scroll-mb-28 scroll-mt-4 ${wide ? '' : 'max-w-3xl'} space-y-3 rounded-lg border bg-card p-4 text-left shadow-sm`}>
      <div className="flex items-center justify-between gap-2 border-b pb-2">
        <h2 className="text-base font-semibold">{title}</h2>
        <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={onClose}>✕ {t('common.close')}</button>
      </div>
      {children}
    </div>
  );
}

export type CampoDialogo = { key: string; label: string; placeholder?: string; multilinea?: boolean; minimo?: number; valor?: string; tipo?: 'text' | 'number' | 'date' };
interface EstadoDialogo {
  titulo: string; detalle?: string; campos: CampoDialogo[]; aceptar: string; cancelar: string | null; peligro?: boolean;
  resolver: (v: Record<string, string> | null) => void;
}

export function useDialogoEnLinea() {
  const { t } = useTranslation();
  const [d, setD] = useState<EstadoDialogo | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const abrir = (x: Omit<EstadoDialogo, 'resolver'>) => new Promise<Record<string, string> | null>((resolver) => {
    setValores(Object.fromEntries(x.campos.filter((c) => c.valor != null).map((c) => [c.key, String(c.valor)])));
    setD({ ...x, resolver });
  });
  const cerrar = (v: Record<string, string> | null) => { d?.resolver(v); setD(null); };
  /** «Sí / No» en la pantalla. true = aceptó. */
  const confirmar = async (titulo: string, opts: { detalle?: string; aceptar?: string; peligro?: boolean } = {}) =>
    (await abrir({ titulo, campos: [], aceptar: opts.aceptar ?? t('dialogo.siContinuar'), cancelar: t('common.cancel'), detalle: opts.detalle, peligro: opts.peligro })) !== null;
  /** Pide uno o más datos. null = canceló. */
  const pedir = (titulo: string, campos: CampoDialogo[], opts: { detalle?: string; aceptar?: string } = {}) =>
    abrir({ titulo, campos, aceptar: opts.aceptar ?? t('dialogo.aceptar'), cancelar: t('common.cancel'), detalle: opts.detalle });
  /** Aviso con un solo botón (reemplaza window.alert). */
  const avisar = async (titulo: string, detalle?: string) => { await abrir({ titulo, detalle, campos: [], aceptar: t('dialogo.entendido'), cancelar: null }); };
  const valido = !!d && d.campos.every((c) => (valores[c.key] ?? '').trim().length >= (c.minimo ?? 1));
  const ui = d ? (
    <PanelIntegrado title={d.titulo} onClose={() => cerrar(null)}>
      <div className="space-y-2 text-sm">
        {d.detalle && <p className="whitespace-pre-line text-muted-foreground">{d.detalle}</p>}
        {d.campos.map((c, i) => (
          <label key={c.key} className="block">
            <span className="mb-1 block text-xs font-medium">{c.label}</span>
            {c.multilinea
              ? <textarea autoFocus={i === 0} rows={3} className="w-full rounded-md border bg-background px-2 py-1" placeholder={c.placeholder}
                  value={valores[c.key] ?? ''} onChange={(e) => setValores({ ...valores, [c.key]: e.target.value })} />
              : <input autoFocus={i === 0} type={c.tipo ?? 'text'} className="h-9 w-full rounded-md border bg-background px-2" placeholder={c.placeholder}
                  value={valores[c.key] ?? ''} onChange={(e) => setValores({ ...valores, [c.key]: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && valido) cerrar(valores); }} />}
          </label>
        ))}
        <div className="flex justify-end gap-2 pt-1">
          {d.cancelar && <Button size="sm" variant="outline" onClick={() => cerrar(null)}>{d.cancelar}</Button>}
          <Button size="sm" variant={d.peligro ? 'destructive' : 'default'} disabled={!valido} onClick={() => cerrar(valores)}>{d.aceptar}</Button>
        </div>
      </div>
    </PanelIntegrado>
  ) : null;
  return { confirmar, pedir, avisar, ui, abierto: !!d };
}
